import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Asset, AssetCursor, LibraryKey, PackagingMode, Paged, Tag, TagGroupDef } from '../types'
import { dropFromPlan, getAssetRepository, isSentence, planFor, smartFor, withRetry, type SearchPlan } from '../services'
import { libraryDef } from '../data/libraries'

interface UseAssetSearchResult {
  assets: Asset[]
  total: number
  tags: Tag[]
  tagsById: Map<string, Tag>
  /** The filter's categories, in order. Data now, not a constant. */
  groups: TagGroupDef[]
  /**
   * Fetch the tags and categories again.
   *
   * They are read once on mount, and the Miro panel does not remount when it is
   * closed and reopened, so without a way to ask again one failed request left
   * the filter empty for the rest of the session.
   */
  reloadTags: () => void
  text: string
  setText: (value: string) => void
  activeTagIds: string[]
  toggleTag: (tagId: string) => void
  /**
   * Show only this tag's pictures -- or, if it is the one already shown, all
   * of them again. (Visual Language used this until 2026-10-05; it now
   * toggles, and several tags narrow to the pictures carrying all of them.)
   */
  onlyTag: (tagId: string) => void
  clearFilters: () => void
  /** Whether packaging, non-packaging, or everything is shown. */
  packaging: PackagingMode
  setPackaging: (mode: PackagingMode) => void
  /** True when anything at all is narrowing the library. */
  hasFilters: boolean
  loading: boolean
  error: string | null
  /** True while a further page is on its way. */
  loadingMore: boolean
  /** True when the library holds more than has been fetched so far. */
  hasMore: boolean
  /** Fetch the next page and add it to what is already on screen. */
  loadMore: () => void
  /**
   * The first `count` images this filter matches, whether or not they have been
   * scrolled to yet. For "select all", which means all of the results and not
   * merely the ones that happen to be loaded.
   */
  fetchMatching: (count: number) => Promise<Asset[]>
  /** A sentence understood (`smart` on): what it means here, shown under the box. */
  plan: SearchPlan | null
  /** True while a sentence is being understood. */
  understanding: boolean
  /** Take one piece of the plan off; the rest is searched again, without asking again. */
  dropPiece: (piece: { tag?: string; word?: string }) => void
}

/** Stable empties, so a switch of library does not hand out a new array every render. */
const NO_ASSETS: Asset[] = []
const NO_TAGS: Tag[] = []
const NO_GROUPS: TagGroupDef[] = []

/**
 * All search and tag-filter logic lives here.
 * Shared by the website and the Miro panel so both behave identically.
 */
export function useAssetSearch(
  initialLimit = 60,
  /** Which library. Audit, unless the page says otherwise. */
  library: LibraryKey = 'audit',
  /**
   * A sentence typed in the box is understood (2026-11-02, as the Internal
   * Library's). Every search box asks for it, the Miro panel's small window
   * too since 2026-11-03.
   */
  options: { smart?: boolean } = {},
): UseAssetSearchResult {
  const smartOn = options.smart === true
  const repository = useMemo(() => getAssetRepository(), [])

  const [text, setText] = useState('')
  const [activeTagIds, setActiveTagIds] = useState<string[]>([])
  const [packaging, setPackaging] = useState<PackagingMode>('all')
  const [assets, setAssets] = useState<Asset[]>([])
  const [total, setTotal] = useState(0)
  const [tags, setTags] = useState<Tag[]>([])
  const [groups, setGroups] = useState<TagGroupDef[]>([])
  /** Bumped to ask for the tags again. */
  const [tagsAttempt, setTagsAttempt] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  /** Where the last page ended, or null when there is no next page. */
  const [cursor, setCursor] = useState<AssetCursor | null>(null)
  const [error, setError] = useState<string | null>(null)
  /**
   * Which library the pictures and the tags on hand belong to (2026-10-05).
   * The Miro panel keeps this hook alive while it switches libraries, and
   * the previous library's pictures used to show for a moment before the new
   * ones arrived. Anything belonging to another library is handed back as
   * nothing, on the very render that switches -- before the request has even
   * gone out -- so there is never a frame of the wrong pictures.
   */
  const [resultsFor, setResultsFor] = useState<LibraryKey | null>(null)
  // Pictures moved out of the library from this page (the Egnyte Inbox's
  // Send to Design Works and Trash): read it again.
  const [changed, setChanged] = useState(0)
  useEffect(() => {
    const again = () => {
      setChanged((n) => n + 1)
      setTagsAttempt((n) => n + 1)
    }
    window.addEventListener('ww:library-changed', again)
    return () => window.removeEventListener('ww:library-changed', again)
  }, [])
  const [tagsFor, setTagsFor] = useState<LibraryKey | null>(null)

  /**
   * Which search the results on screen belong to.
   *
   * A page can be in flight when the filters change. Without this, that page
   * arrives late and is appended to a completely different search — the classic
   * way an infinite list ends up showing two searches at once.
   */
  const generation = useRef(0)

  // Tags and the categories they sit in are read together: either is useless
  // without the other, and asking twice is two round trips to draw one filter.
  //
  // Retried, because this runs once and the thing that reads it may live for
  // hours. A single refused request used to empty the filter for good.
  useEffect(() => {
    let cancelled = false
    withRetry(() => Promise.all([repository.listTags(library), repository.listTagGroups(library)]))
      .then(([tagList, groupList]) => {
        if (cancelled) return
        setTags(tagList)
        setGroups(groupList)
        setTagsFor(library)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load tags')
      })
    return () => {
      cancelled = true
    }
  }, [repository, tagsAttempt, library])

  // Debounce the query by 200ms so we do not search on every keystroke
  const [debouncedText, setDebouncedText] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedText(text), 200)
    return () => clearTimeout(timer)
  }, [text])

  /*
   * The sentence, understood. Typed as you go, so it waits until the typing
   * has stopped for a moment before asking; until the answer is in, nothing
   * is searched for the sentence (it would only find nothing). `planned` is
   * which text the plan belongs to: null plan and that text means "searched
   * plainly" -- not understood, or every piece taken off.
   */
  const [plan, setPlan] = useState<SearchPlan | null>(null)
  const [planned, setPlanned] = useState<string | null>(null)
  const sentence = smartOn && isSentence(debouncedText)
  const understanding = sentence && planned !== debouncedText
  useEffect(() => {
    if (!sentence) {
      setPlan(null)
      setPlanned(null)
      return
    }
    let cancelled = false
    setPlanned(null)
    const timer = setTimeout(() => {
      void planFor(debouncedText, [library]).then((found) => {
        if (cancelled) return
        setPlan(found)
        setPlanned(debouncedText)
      })
    }, 500)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [sentence, debouncedText, library])
  const dropPiece = useCallback((piece: { tag?: string; word?: string }) => {
    setPlan((now) => (now ? dropFromPlan(now, piece) : now))
  }, [])
  /** What this library is searched with under the plan; undefined for a plain search, null for nothing. */
  const smart = plan ? smartFor(plan, library) : undefined
  const smartKey = JSON.stringify(smart ?? '')

  // Only this library's tags, never the last one's (see `tagsFor`).
  const shownTags = tagsFor === library ? tags : NO_TAGS
  const shownGroups = tagsFor === library ? groups : NO_GROUPS
  const tagsById = useMemo(() => new Map(shownTags.map((t) => [t.id, t])), [shownTags])

  /**
   * The chosen tags, split by the category each one belongs to.
   *
   * This is what carries the OR-within / AND-across rule down to the
   * repository: it cannot work that out from a flat list of ids, because the
   * ids alone do not say which came from the same row of the filter.
   *
   * Serialised to a string for the effect's dependencies — a fresh array every
   * render would restart the search on every render.
   */
  // A library without categories (Visual Language) keeps all its tags in one
  // hidden category, which would make several chosen tags an OR. There each
  // tag is its own group instead, so several chosen tags narrow to the
  // pictures that carry every one of them (Huanrui, 2026-10-05).
  const everyTag = !libraryDef(library).hasCategories || libraryDef(library).andWithin === true
  const tagGroups = useMemo(() => {
    if (everyTag) return activeTagIds.map((id) => [id])
    const byGroup = new Map<string, string[]>()
    for (const id of activeTagIds) {
      const group = tagsById.get(id)?.group ?? 'other'
      byGroup.set(group, [...(byGroup.get(group) ?? []), id])
    }
    return [...byGroup.values()]
  }, [activeTagIds, tagsById, everyTag])
  const tagGroupsKey = JSON.stringify(tagGroups)

  useEffect(() => {
    let cancelled = false
    generation.current += 1
    setLoading(true)
    setError(null)
    // A "load more" still on its way belongs to the search before this one:
    // it will not report back (its generation is gone), so it must not be
    // left holding the button at "Loading…" for ever (2026-10-13).
    setLoadingMore(false)
    // A sentence still being understood: wait for it.
    if (understanding) return
    // Understood, and nothing in it for this library.
    if (smart === null) {
      setAssets([])
      setTotal(0)
      setCursor(null)
      setResultsFor(library)
      setLoading(false)
      return
    }

    withRetry(() =>
      repository.search({ text: debouncedText, tagGroups, packaging, limit: initialLimit, library, smart }),
    )
      .then((page) => {
        if (cancelled) return
        setAssets(page.items)
        setTotal(page.total)
        setCursor(page.next ?? null)
        setResultsFor(library)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Search failed. Please try again.')
        setAssets([])
        setTotal(0)
        setCursor(null)
        setResultsFor(library)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // tagGroups is rebuilt every render; the string is what actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repository, debouncedText, tagGroupsKey, packaging, initialLimit, library, changed, understanding, smartKey])

  // The database says whether there is a next page. Comparing counts would
  // guess, and guess wrong the moment two images share a timestamp.
  const hasMore = cursor !== null

  const loadMore = useCallback(() => {
    if (loading || loadingMore) return
    if (!cursor) return

    const mine = generation.current
    setLoadingMore(true)
    repository
      .search({
        text: debouncedText,
        tagGroups,
        packaging,
        limit: initialLimit,
        after: cursor,
        library,
        smart,
      })
      .then((page) => {
        if (generation.current !== mine) return
        setAssets((prev) => {
          // A page can overlap what is already here if something was added to
          // the library between one request and the next. Adding the same image
          // twice would give it two cards and break selection, so the ids are
          // checked rather than trusted.
          const already = new Set(prev.map((asset) => asset.id))
          return [...prev, ...page.items.filter((asset) => !already.has(asset.id))]
        })
        /*
         * THE TOTAL IS NOT TOUCHED HERE, AND THAT IS THE FIX.
         *
         * It used to be overwritten from every page, which is wrong in
         * principle — a count of what the search matches belongs to the search,
         * not to a request whose job is to fetch the next slice — and wrong in
         * practice in one exact case.
         *
         * A tag holding exactly one full page returns a full page and a cursor,
         * because the server cannot know there is nothing after it. Scrolling
         * to the bottom then asks for a page that turns out to be empty, and an
         * empty page's total is zero. The select bar is drawn only when the
         * total is above zero, so it vanished — at 60, at 120, at any multiple
         * of the page size, and never otherwise, which is why it looked like
         * chance.
         *
         * A total that is stale after the library grows mid-scroll is a much
         * smaller lie than one that says nothing matched.
         */
        setCursor(page.next ?? null)
      })
      .catch((err: unknown) => {
        if (generation.current !== mine) return
        setError(err instanceof Error ? err.message : 'Could not load more images')
      })
      .finally(() => {
        if (generation.current === mine) setLoadingMore(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repository, debouncedText, tagGroupsKey, packaging, initialLimit, cursor, loading, loadingMore, library, smartKey])

  /**
   * How many rows one request asks for while collecting a large selection.
   *
   * Not a ceiling on the selection — a page size. This used to be a hard limit
   * of a thousand, which meant the button said "All 1000" on a library of 1437
   * and was simply wrong. "Select all" has to mean all, so the pages are walked
   * instead: the request stays an ordinary size and the loop is what decides
   * when to stop.
   */
  const BULK_PAGE = 1000

  const fetchMatching = useCallback(
    async (count: number): Promise<Asset[]> => {
      const collected: Asset[] = []
      let after: AssetCursor | null = null

      // `next` is null on the last page, which is the only reliable end: the
      // database knows, and counting rows against a total would go wrong the
      // moment an import lands mid-loop.
      if (smart === null) return []
      while (collected.length < count) {
        const page: Paged<Asset> = await repository.search({
          text: debouncedText,
          tagGroups,
          packaging,
          limit: Math.min(count - collected.length, BULK_PAGE),
          after,
          library,
          smart,
        })
        collected.push(...page.items)
        if (!page.next || page.items.length === 0) break
        after = page.next
      }

      // A page can overshoot when the last request rounded up.
      return collected.length > count ? collected.slice(0, count) : collected
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [repository, debouncedText, tagGroupsKey, packaging, library, smartKey],
  )

  // Stable, because a listener elsewhere depends on it and a fresh function
  // every render would resubscribe on every render.
  const reloadTags = useCallback(() => setTagsAttempt((n) => n + 1), [])

  const toggleTag = useCallback((tagId: string) => {
    setActiveTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    )
  }, [])

  const onlyTag = useCallback((tagId: string) => {
    setActiveTagIds((prev) => (prev.length === 1 && prev[0] === tagId ? [] : [tagId]))
  }, [])

  const clearFilters = useCallback(() => {
    setActiveTagIds([])
    setText('')
    setPackaging('all')
  }, [])


  // Only this library's pictures, never the last one's (see `resultsFor`).
  const current = resultsFor === library

  return {
    assets: current ? assets : NO_ASSETS,
    total: current ? total : 0,
    tags: shownTags,
    tagsById,
    groups: shownGroups,
    reloadTags,
    text,
    setText,
    activeTagIds,
    toggleTag,
    onlyTag,
    clearFilters,
    packaging,
    setPackaging,
    hasFilters: activeTagIds.length > 0 || text.trim().length > 0 || packaging !== 'all',
    loading: loading || !current,
    error,
    loadingMore,
    hasMore: current && hasMore,
    loadMore,
    fetchMatching,
    plan: sentence ? plan : null,
    understanding,
    dropPiece,
  }
}
